/* eslint-disable import/no-cycle */
import React, { createRef } from 'react'

import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { v4 as uuid } from 'uuid'

import { Checkbox } from 'components/checkbox'
import Input from 'components/input'
import Link from 'components/link'
import Message from 'components/message'
import { ReCaptcha, loadRecaptcha } from 'components/recaptchaV2'
import theme from 'components/theme'
import { isMobileOrTablet, onEnterKeyPress } from 'helpers/utils'
import { alphanumeric, pnr, validateField } from 'helpers/validations'
import { parseToGeneralLanguage } from 'localization/helpers'
import { mapMediaType } from 'selectors/mediaType'
import { getReservationLoadingError } from 'selectors/reservation'

import ReservationError from './reservation-error'
import {
  Button,
  Container,
  IconWithMargin,
  InputWrapper,
  Label,
  MessageContainer,
  SwitcherContainer,
  Text,
  Title
} from './styled'

class ReservationSearchBox extends React.Component {
  state = {
    pnrValue: '',
    lastNameValue: '',
    errorPnrValue: '',
    errorLastNameValue: '',
    conditionCheckbox: false,
    errorConditionValue: '',
    clicked: false
  }

  constructor(props) {
    super(props)
    this.codeInputId = uuid()
    this.passengerInputId = uuid()
    this.boxTop = createRef()
    this.recaptchaRef = createRef()
  }

  componentDidMount() {
    this.scrollToComponent = require('react-scroll-to-component')

    const selectedLanguage = parseToGeneralLanguage(this.props.i18n.language)
    loadRecaptcha(selectedLanguage)
    if (this.props.reservationLoadingError && this.boxTop) {
      this.scrollToComponent(this.boxTop.current, {
        offset: 0,
        align: 'middle',
        duration: 800,
        ease: 'inOutQuad'
      })
    }
  }

  validateFields = () => {
    let areFieldsValid = true
    if (!this.state.pnrValue || !validateField(pnr, this.state.pnrValue)) {
      this.setState({ errorPnrValue: this.props.labels.pnr.error })
      areFieldsValid = false
    }
    if (!this.state.lastNameValue) {
      this.setState({ errorLastNameValue: this.props.labels.lastName.error })
      areFieldsValid = false
    }

    if (!this.state.conditionCheckbox && this.props.isCheckIn) {
      this.setState({ errorConditionValue: this.props.labels.conditions.error })
      areFieldsValid = false
    }
    return areFieldsValid
  }

  handleFirstValueChange = ({ target: { value } }) => {
    if (!value || (validateField(alphanumeric, value) && value && value.length <= 6)) {
      this.setState({
        pnrValue: value.toUpperCase(),
        errorPnrValue: ''
      })
    }
  }

  handleSecondValueChange = ({ target: { value } }) => {
    this.setState({
      lastNameValue: value,
      errorLastNameValue: ''
    })
  }

  handleConditionCheckbox = e => {
    this.setState({
      conditionCheckbox: e.checked,
      errorConditionValue: ''
    })
  }

  handleSubmit = () => {
    this.props.onSearch(this.state.pnrValue, this.state.lastNameValue)
  }

  handleButtonClick = () => {
    if (this.validateFields()) {
      this.setState({ clicked: true })
      this.recaptchaRef.current?.execute()
    }
  }

  render() {
    return (
      <div>
        <Container ref={this.boxTop} e2eClass={this.props.labels.codeBox}>
          <Title>{this.props.labels.title}</Title>
          <InputWrapper
            e2eClass={this.props.labels.pnr.codeBox}
            error={this.state.errorLastNameValue && !this.state.errorPnrValue}
          >
            <Label htmlFor={this.codeInputId} e2eClass={this.props.labels.pnr.codeTitle}>
              {this.props.labels.pnr.label}
            </Label>
            <Input
              id={`${this.codeInputId}`}
              type={'text'}
              leftIcon={
                <IconWithMargin
                  name={this.props.labels.pnr.iconName}
                  color={theme.colors.blue}
                  size={30}
                />
              }
              onKeyPress={event =>
                onEnterKeyPress(event, () => {
                  event.currentTarget.blur()
                  this.handleButtonClick()
                })
              }
              value={this.state.pnrValue}
              onChange={this.handleFirstValueChange}
              placeholder={
                isMobileOrTablet(this.props.mediaType)
                  ? this.props.labels.pnr.label
                  : this.props.labels.pnr.labelMobile
              }
              name={this.props.labels.pnr.code}
              error={this.state.errorPnrValue}
              boxShadow
            />
          </InputWrapper>
          <InputWrapper
            e2eClass={this.props.labels.lastName.codeBox}
            error={this.state.errorPnrValue && !this.state.errorLastNameValue}
          >
            <Label htmlFor={this.passengerInputId} e2eClass={this.props.labels.lastName.codeTitle}>
              {this.props.labels.lastName.label}
            </Label>
            <Input
              id={this.passengerInputId}
              type={'text'}
              leftIcon={
                <IconWithMargin
                  name={this.props.labels.lastName.iconName}
                  color={theme.colors.blue}
                  size={30}
                />
              }
              onKeyPress={event =>
                onEnterKeyPress(event, () => {
                  event.currentTarget.blur()
                  this.handleButtonClick()
                })
              }
              value={this.state.lastNameValue}
              onChange={this.handleSecondValueChange}
              placeholder={this.props.labels.lastName.placeholder}
              error={this.state.errorLastNameValue}
              name={this.props.labels.lastName.code}
              boxShadow
            />
          </InputWrapper>

          {this.props.isCheckIn && (
            <SwitcherContainer>
              <Checkbox
                name="checkt"
                onChange={this.handleConditionCheckbox}
                checked={this.state.conditionCheckbox}
              >
                <Text>{this.props.t('general.search-box-container.check-in.read-and-agree')}</Text>
                <Link href={this.props.t('checkin-conditions.url')} target="_blank">
                  {this.props.t('general.search-box-container.check-in.conditions')}
                </Link>
                <Text>{this.props.t('general.search-box-container.check-in.commodity')}</Text>
              </Checkbox>
              {this.state.errorConditionValue && (
                <MessageContainer>
                  <Message type="formInputError">{this.state.errorConditionValue}</Message>
                </MessageContainer>
              )}
            </SwitcherContainer>
          )}

          <Button
            e2eClass={this.props.labels.searchButton}
            error={this.state.errorPnrValue || this.state.errorLastNameValue}
            onClick={this.handleButtonClick}
            large
          >
            {this.props.labels.search}
          </Button>
        </Container>
        {this.props.reservationLoadingError && this.state.clicked && <ReservationError />}
        <ReCaptcha ref={this.recaptchaRef} onSuccess={this.handleSubmit} badge="bottomleft" />
      </div>
    )
  }
}

const mapStateToProps = state => ({
  mediaType: mapMediaType(state),
  reservationLoadingError: getReservationLoadingError(state)
})

export default withTranslation(null, { withRef: true })(
  connect(mapStateToProps, null, null, { forwardRef: true })(ReservationSearchBox)
)
